import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { usePromotions } from "@/features/fee-engine/api/use-promotions";
import type { PromotionFilter } from "@/features/fee-engine/api/dto";
import {
  PromotionFormDialog,
  promotionsColumns,
  type PromotionRow,
} from "@/features/fee-engine/components";
import { useFeeEngineTranslation } from "@/features/fee-engine/i18n/use-fee-engine-translation";

const toPromotionFilter = (filters: ColumnFiltersState): PromotionFilter => {
  const filter: PromotionFilter = {};
  for (const { id, value } of filters) {
    if (typeof value !== "string" || !value) continue;
    if (id === "code") filter.code = value;
    if (id === "productCode") filter.productCode = value;
    if (id === "status") filter.status = value as PromotionFilter["status"];
  }
  return filter;
};

/**
 * Liste paginée des promotions (`POST /Promotions/all`, guide §6.5).
 * Écriture réservée aux rôles flashpay-superadmin / flashpay-admin.
 */
export function PromotionsPage() {
  const { t } = useFeeEngineTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      filter: toPromotionFilter(columnFilters),
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [columnFilters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } = usePromotions(params);

  return (
    <section className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("fees.promotions.title")}
        </h1>
        <RequireRole
          roles={[FLASHPAY_ROLES.SUPERADMIN, FLASHPAY_ROLES.ADMIN]}
        >
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 w-4 h-4" />
            {t("fees.promotions.create")}
          </Button>
        </RequireRole>
      </div>

      <DataTableClar
        data={(data?.items ?? []).filter((p): p is PromotionRow => Boolean(p.id))}
        columnsClar={promotionsColumns}
        title={t("fees.promotions.title")}
        exportFileName="promotions"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <PromotionFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </section>
  );
}
